import React, { useState } from "react";
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  Box,
  Alert,
} from "@mui/material";
import { Dashboard, dashboardShareAPI } from "../../services/api/api";
import ShareModal, { ExistingShare } from "../common/ShareModal";

interface ShareDashboardModalProps {
  open: boolean;
  dashboard: Dashboard | null;
  onClose: () => void;
  /** Function to capture current financial data for snapshot mode */
  captureSnapshotData?: () => Promise<object>;
}

const ShareDashboardModal: React.FC<ShareDashboardModalProps> = ({
  open,
  dashboard,
  onClose,
  captureSnapshotData,
}) => {
  const [dataMode, setDataMode] = useState<"live" | "snapshot">("live");

  const handleCreateShare = async (
    password: string,
    expiresAt: string | null
  ): Promise<{ shareId: string }> => {
    if (!dashboard) throw new Error("No dashboard selected");

    let snapshotData: object | undefined;
    if (dataMode === "snapshot" && captureSnapshotData) {
      snapshotData = await captureSnapshotData();
    }

    return await dashboardShareAPI.create({
      dashboardId: dashboard.id,
      password,
      expiresAt,
      isLive: dataMode === "live",
      snapshotData,
    });
  };

  const handleListShares = async (): Promise<ExistingShare[]> => {
    if (!dashboard) return [];
    const shares = await dashboardShareAPI.listForDashboard(dashboard.id);
    return shares.map((s) => ({
      id: s.id,
      createdAt: s.createdAt,
      expiresAt: s.expiresAt,
    }));
  };

  const handleDeleteShare = async (shareId: string): Promise<void> => {
    await dashboardShareAPI.delete(shareId);
  };

  const buildShareUrl = (shareId: string): string => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/shared/dashboards/${shareId}`;
  };

  // Reset data mode when modal opens
  React.useEffect(() => {
    if (open) {
      setDataMode("live");
    }
  }, [open]);

  if (!dashboard) {
    return null;
  }

  const additionalOptions = (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Data Mode
      </Typography>
      <FormControl component="fieldset">
        <RadioGroup
          value={dataMode}
          onChange={(e) => setDataMode(e.target.value as "live" | "snapshot")}
        >
          <FormControlLabel
            value="live"
            control={<Radio size="small" />}
            label={
              <Box>
                <Typography variant="body2">Live Data (Default)</Typography>
                <Typography variant="caption" color="text.secondary">
                  Viewers see real-time financial data whenever they access the
                  link
                </Typography>
              </Box>
            }
          />
          <FormControlLabel
            value="snapshot"
            control={<Radio size="small" />}
            label={
              <Box>
                <Typography variant="body2">Snapshot</Typography>
                <Typography variant="caption" color="text.secondary">
                  Viewers see data frozen at the time you create this share
                </Typography>
              </Box>
            }
          />
        </RadioGroup>
      </FormControl>
      {dataMode === "snapshot" && (
        <Alert severity="info" sx={{ mt: 1 }}>
          A snapshot of your current financial data will be captured when you
          generate the link.
        </Alert>
      )}
    </Box>
  );

  return (
    <ShareModal
      open={open}
      onClose={onClose}
      title={`Share Dashboard: ${dashboard.name}`}
      itemDescription="this dashboard"
      createShare={handleCreateShare}
      listShares={handleListShares}
      deleteShare={handleDeleteShare}
      buildShareUrl={buildShareUrl}
      additionalOptions={additionalOptions}
    />
  );
};

export default ShareDashboardModal;
